import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getMeCached } from './authCache';
import { getSchoolInfoCached } from './api/schoolInfo';
import { isFeatureVisible } from './featureVisibility';
import PrivacyNoticeGate from '../components/PrivacyNoticeGate';

// Guard for admin/teacher/superadmin routes using /admin/me
const RequireAdmin = ({ children, featureKey = null }) => {
  const location = useLocation();
  const [status, setStatus] = useState('checking'); // 'checking' | 'allowed' | 'login' | 'unauthorized'

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      try {
        let result = await getMeCached();
        // Second chance with a forced fetch in case an earlier unauthenticated
        // /admin/me response was cached during login bootstrap.
        if (!result?.data?.admin_user) {
          result = await getMeCached(true);
        }
        const { ok, data } = result || {};
        const role = String(data?.admin_user?.role || '').toLowerCase();
        const schoolId = data?.admin_user?.school_id || data?.admin_user?.school || data?.admin_user?.school_name;
        const allowed = role === 'superadmin' || role === 'admin' || role === 'teacher';

        if (allowed && featureKey && role !== 'superadmin' && schoolId) {
          const schoolInfo = await getSchoolInfoCached(schoolId);
          const featureAllowed = isFeatureVisible(schoolInfo?.settings || {}, featureKey, role);
          if (!featureAllowed) {
            if (!cancelled) {
              setStatus('unauthorized');
            }
            return;
          }
        }

        if (!cancelled) {
          if (data && data.admin_user && allowed) setStatus('allowed');
          else if (data && data.admin_user && !allowed) setStatus('unauthorized');
          else if (ok) setStatus('login');
          else setStatus('login');
        }
      } catch (_) {
        if (!cancelled) setStatus('login');
      }
    }
    verify();
    return () => { cancelled = true; };
  }, [featureKey]);

  if (status === 'checking') return null; // prevent intermediate flashes
  if (status === 'login') return <Navigate to={{ pathname: '/admin', search: '?reason=login_required' }} replace state={{ from: location }} />;
  if (status === 'unauthorized') return <Navigate to={{ pathname: '/admin/unauthorized' }} replace state={{ from: location }} />;
  return <PrivacyNoticeGate>{children}</PrivacyNoticeGate>;
};

export default RequireAdmin;
